import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'
import type { ColumnDef, Row } from '@tanstack/react-table'
import classNames from 'classnames'
import { Suspense } from 'react'
import React from 'react'

import Image from '@/components/Atoms/Image/Image'
import PriceUnavailable from '@/components/Feed/PriceUnavailable/PriceUnavailable'
import ProductArt from '@/components/Feed/ProductArt/ProductArt'
import ProductColumn from '@/components/Feed/ProductColumn/ProductColumn'
import ProductInfo from '@/components/Feed/ProductInfo/ProductInfo'
import ProductName from '@/components/Feed/ProductName/ProductName'
import UpdatedAtTooltip from '@/components/Feed/UpdatedAtTooltip/UpdatedAtTooltip'
import TableColumnFilter from '@/components/Filters/TableColumnFilter/TableColumnFilter'
import { SCENARIOS } from '@/constants'
import { useFeedPriceSegments } from '@/hooks/search/useFeedPriceSegments'
import DeliveryTimeHint from '@/pages/Feed/DeliveryTimeHint/DeliveryTimeHint'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  FormattedFeedProductType,
  PriceSegmentOption,
  StoreShortType,
} from '@/types'
import { safeLazy } from '@/utils/app/safeLazy'
import { hasRoleFromGroup } from '@/utils/auth/roleGroups'
import { isBuyerUser, isSupplierStaff } from '@/utils/auth/userRoles'
import {
  formatDeliveryTime,
  IN_STOCK_DELIVERY_LABEL,
} from '@/utils/format/formatDeliveryTime'
import { formatStockQuantity } from '@/utils/format/formatStockQuantity'
import { formatPriceWithVat } from '@/utils/price/vat'
import { PRICE_UNAVAILABLE_TEXT } from '@/utils/products/offerAvailability'
import { getRowOfferPrice } from '@/utils/products/offerPrice'
import { focusProductSearchInput } from '@/utils/search/ui/focusProductSearchInput'

import cn from './Feed.module.scss'
import { SegmentPriceHeader } from './SegmentPriceColumns/SegmentPriceColumns'
import { getSegmentPriceColumns } from './SegmentPriceColumns/segmentPriceColumns.config'

const AddToCartModal = safeLazy(
  () => import('@/components/Feed/AddToCartModal/AddToCartModal')
)

const CartButtonCell = React.memo(
  ({ product }: { product: FormattedFeedProductType }) => {
    const scenario = useUserInfoStore((state) => state.scenario)
    const hasStock = product.suppliersStock?.[0]?.stock?.[0]?.quantity

    if (!hasStock || scenario === SCENARIOS.CLIENT_SELECTION_PENDING)
      return null

    return (
      <Suspense fallback={<div className={cn.cartActionPlaceholder} />}>
        <AddToCartModal product={product} />
      </Suspense>
    )
  }
)

CartButtonCell.displayName = 'CartButtonCell'

const QuickAddCell = React.memo(
  ({ product }: { product: FormattedFeedProductType }) => {
    const scenario = useUserInfoStore((state) => state.scenario)

    if (scenario === SCENARIOS.CLIENT_SELECTION_PENDING) return null

    return (
      <Suspense fallback={<div className={cn.quickAddPlaceholder} />}>
        <AddToCartModal product={product} quickAddOnly />
      </Suspense>
    )
  }
)

QuickAddCell.displayName = 'QuickAddCell'

const ImageCell = React.memo(
  ({
    product,
    isParent,
  }: {
    product: FormattedFeedProductType['nomenclatureDto']
    isParent: boolean | undefined
  }) => {
    if (!isParent) return null

    return (
      <div className={cn.imageBlock}>
        <ProductInfo product={product}>
          <Image productCode={product.code} />
        </ProductInfo>
      </div>
    )
  },
  (prevProps, nextProps) =>
    prevProps.product.code === nextProps.product.code &&
    prevProps.isParent === nextProps.isParent
)

ImageCell.displayName = 'ImageCell'

const NOT_AVAILABLE = '-'

const getStockItem = (record: FormattedFeedProductType) =>
  record.suppliersStock[0]?.stock[0]

export function getColumnValue(
  record: FormattedFeedProductType,
  prop: string
): string | number | StoreShortType | null | undefined {
  const stockItem = getStockItem(record)
  const price = getRowOfferPrice(record)

  if (prop === 'basicPrice') return price?.purchasePrice ?? null
  if (prop === 'retailPrice') return price?.retailPrice ?? null
  if (prop === 'sellingPrice') return price?.sellingPrice ?? null

  if (!stockItem) return undefined

  if (prop === 'deliveryTime') {
    return formatDeliveryTime(
      stockItem.effectiveEtaMinDays,
      stockItem.effectiveEtaMaxDays
    )
  }

  const standardFields: Record<string, unknown> = {
    storeShortModel: stockItem.storeShortModel,
    quantity: stockItem.quantity,
    quantityInCart: stockItem.quantityInCart,
    effectiveEtaMinDays: stockItem.effectiveEtaMinDays,
    effectiveEtaMaxDays: stockItem.effectiveEtaMaxDays,
  }

  return (standardFields[prop] ??
    (stockItem as unknown as Record<string, unknown>)[prop]) as ReturnType<
    typeof getColumnValue
  >
}

export function getSellingPrice(
  record: FormattedFeedProductType
): number | null {
  return getRowOfferPrice(record)?.sellingPrice ?? null
}

function formatPrice(price: number | null | undefined): string {
  if (price == null) return PRICE_UNAVAILABLE_TEXT
  return formatPriceWithVat(Number(price))
}

function getUnit(record: FormattedFeedProductType): string {
  return record.nomenclatureDto.unitOfMeasurementDto?.description ?? ''
}

const PriceHeader = React.memo(
  ({
    title,
    visibilityKey,
    toggleKey,
    noToggle = false,
  }: {
    title: string
    visibilityKey: 'isBasicPriceVisible' | 'isSegmentPriceVisible'
    toggleKey: 'setIsBasicPriceVisible' | 'setIsSegmentPriceVisible'
    noToggle?: boolean
  }) => {
    const visible = useFeedStore((state) => state[visibilityKey])
    const toggle = useFeedStore((state) => state[toggleKey])

    if (noToggle) return <>{title}</>

    return (
      <div
        className={cn.visibilityToggleHeader}
        onClick={(e) => {
          e.stopPropagation()
          toggle(!visible)
          focusProductSearchInput()
        }}
      >
        {title} {visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
      </div>
    )
  }
)

PriceHeader.displayName = 'PriceHeader'

const BasicPriceCell = React.memo(
  ({ row }: { row: Row<FormattedFeedProductType> }) => {
    const isBasicPriceVisible = useFeedStore(
      (state) => state.isBasicPriceVisible
    )
    const isClient = useUserInfoStore((state) => isBuyerUser(state.tokenRoles))

    const price = formatPrice(
      getColumnValue(row.original, 'basicPrice') as number
    )

    return (
      <span className={cn.boldCell}>
        {isClient || isBasicPriceVisible ? price : '***'}
      </span>
    )
  }
)

BasicPriceCell.displayName = 'BasicPriceCell'

const SegmentPriceCell = React.memo(
  ({ row }: { row: Row<FormattedFeedProductType> }) => {
    const isSegmentPriceVisible = useFeedStore(
      (state) => state.isSegmentPriceVisible
    )
    const isClient = useUserInfoStore((state) => isBuyerUser(state.tokenRoles))

    const hasSelectedSegments = useFeedPriceSegments().length > 0
    const cellClassName = classNames(
      cn.boldCell,
      hasSelectedSegments && cn.contextSegmentCell
    )

    if (!isClient && !isSegmentPriceVisible) {
      return <span className={cellClassName}>***</span>
    }

    const price = getRowOfferPrice(row.original)
    const sellingPrice = getSellingPrice(row.original)

    return (
      <span className={cellClassName}>
        {sellingPrice === null ? (
          <PriceUnavailable price={price} />
        ) : (
          <UpdatedAtTooltip
            updatedAt={price?.priceUpdatedAt}
            kind="price"
            priceSource={price?.priceSource}
          >
            {formatPriceWithVat(sellingPrice)}
          </UpdatedAtTooltip>
        )}
      </span>
    )
  }
)

SegmentPriceCell.displayName = 'SegmentPriceCell'

export const getColumns = (
  showColumnFilters = true,
  priceSegments: PriceSegmentOption[] = []
): ColumnDef<FormattedFeedProductType, unknown>[] => {
  return [
    {
      id: 'productArt',
      header: 'Артикул',
      cell: ({ row }) => <ProductArt product={row.original} />,
    },
    {
      id: 'productImages',
      header: 'Фото',
      cell: ({ row }) => (
        <ImageCell
          product={row.original.nomenclatureDto}
          isParent={row.original.parent}
        />
      ),
    },
    {
      id: 'brand',
      header: () =>
        showColumnFilters ? (
          <TableColumnFilter title="Бренд" filterType="brands" />
        ) : (
          'Бренд'
        ),
      cell: ({ row }) => (
        <span>
          {row.original.parent
            ? row.original.nomenclatureDto.brandDto?.description
            : ''}
        </span>
      ),
    },
    {
      id: 'productName',
      header: 'Наименование',
      cell: ({ row }) => <ProductName formattedProduct={row.original} />,
    },
    {
      id: 'quantity',
      header: 'Наличие',
      cell: ({ row }) => {
        const stockItem = getStockItem(row.original)
        const quantity = stockItem?.quantity
        const unit = getUnit(row.original)
        const displayValue = formatStockQuantity(
          quantity,
          unit,
          stockItem?.displayRules,
          NOT_AVAILABLE
        )
        return (
          <span className={cn.boldCell}>
            <UpdatedAtTooltip
              updatedAt={stockItem?.stockUpdatedAt}
              kind="stock"
            >
              <ProductColumn value={displayValue} />
            </UpdatedAtTooltip>
          </span>
        )
      },
    },
    {
      id: 'deliveryTime',
      header: 'Срок доставки',
      cell: ({ row }) => {
        const delivery = getColumnValue(row.original, 'deliveryTime')
        if (delivery === IN_STOCK_DELIVERY_LABEL) {
          return (
            <span className={cn.inStockDelivery}>
              {IN_STOCK_DELIVERY_LABEL}
            </span>
          )
        }
        const value = String(delivery ?? NOT_AVAILABLE)
        if (value === NOT_AVAILABLE) return <ProductColumn value={value} />

        return (
          <DeliveryTimeHint className={cn.deliveryTimeHint}>
            <ProductColumn value={value} />
          </DeliveryTimeHint>
        )
      },
    },
    {
      id: 'basicPrice',
      header: () => (
        <PriceHeader
          title="Закупочная цена"
          visibilityKey="isBasicPriceVisible"
          toggleKey="setIsBasicPriceVisible"
        />
      ),
      cell: ({ row }) => <BasicPriceCell row={row} />,
    },
    {
      id: 'segment',
      header: () => <SegmentPriceHeader />,
      cell: ({ row }) => <SegmentPriceCell row={row} />,
    },
    ...getSegmentPriceColumns(priceSegments),
    {
      id: 'storeId',
      header: () =>
        showColumnFilters ? (
          <TableColumnFilter title="Склад" filterType="stores" />
        ) : (
          'Склад'
        ),
      cell: ({ row }) => {
        const store = getColumnValue(
          row.original,
          'storeShortModel'
        ) as StoreShortType
        return <span>{store?.description ?? NOT_AVAILABLE}</span>
      },
    },
    {
      id: 'retailPrice',
      header: 'РРЦ',
      cell: ({ row }) => (
        <span className={cn.boldCell}>
          {formatPrice(getColumnValue(row.original, 'retailPrice') as number)}
        </span>
      ),
    },
    {
      id: 'quickAdd',
      header: '',
      cell: ({ row }) => <QuickAddCell product={row.original} />,
    },
    {
      id: 'cartButton',
      header: '',
      cell: ({ row }) => <CartButtonCell product={row.original} />,
    },
  ]
}

function getColumnKeys(excludeKeys: string[] = []): string[] {
  return getColumns()
    .map((column) => column.id as string)
    .filter((id) => !excludeKeys.includes(id))
}

const ALL_COLUMN_KEYS = getColumnKeys()
const COLUMN_KEYS_WITHOUT_PURCHASE_PRICE = getColumnKeys(['basicPrice'])

export const getColumnsKeys = (
  tokenRoles: readonly string[]
): string[] | undefined => {
  if (hasRoleFromGroup(tokenRoles, 'PURCHASE_VIEWERS')) return ALL_COLUMN_KEYS
  if (isSupplierStaff(tokenRoles) || isBuyerUser(tokenRoles)) {
    return COLUMN_KEYS_WITHOUT_PURCHASE_PRICE
  }
  return undefined
}
